import { useState, type FormEvent } from 'react';
import { useAuth } from './AuthProvider';

type Mode = 'sign-in' | 'forgot-password';

export function LoginPage() {
    const { signIn, sendPasswordResetEmail } = useAuth();
    const [mode, setMode] = useState<Mode>('sign-in');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [resetSent, setResetSent] = useState(false);

    function switchMode(newMode: Mode) {
        setMode(newMode);
        setError(null);
        setPassword('');
        setResetSent(false);
    }

    async function handleSignIn(e: FormEvent) {
        e.preventDefault();
        setSubmitting(true);
        setError(null);
        const { error: signInError } = await signIn(email.trim(), password);
        setSubmitting(false);
        if (signInError) {
            setError(signInError.message);
        }
    }

    async function handleSendReset(e: FormEvent) {
        e.preventDefault();
        setSubmitting(true);
        setError(null);
        const { error: resetError } = await sendPasswordResetEmail(email.trim());
        setSubmitting(false);
        if (resetError) {
            setError(resetError.message);
        } else {
            // Supabase intentionally does not reveal whether the email exists.
            // Show the same success state regardless to avoid leaking user info.
            setResetSent(true);
        }
    }

    return (
        <div className="min-h-screen flex flex-col bg-graphite-50 dark:bg-graphite-950">
            <div className="flex-1 flex items-center justify-center px-6">
                <div className="w-full max-w-sm">
                    <div className="mb-10">
                        <div className="flex items-baseline gap-3 mb-2">
                            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-graphite-500">
                                PPC / Internal
                            </span>
                            <span className="h-px flex-1 bg-graphite-200 dark:bg-graphite-800" />
                        </div>
                        <h1 className="font-display font-bold text-3xl text-graphite-900 dark:text-graphite-50 tracking-tight">
                            {mode === 'sign-in' ? 'Scheduling' : 'Reset Password'}
                        </h1>
                        <p className="font-mono text-xs text-graphite-500 mt-1">
                            {mode === 'sign-in'
                                ? 'Precision Performance Concrete'
                                : "We'll email you a link to set a new password."}
                        </p>
                    </div>

                    {mode === 'sign-in' ? (
                        <form onSubmit={handleSignIn} className="space-y-4">
                            <div>
                                <label
                                    htmlFor="email"
                                    className="block font-mono text-[10px] uppercase tracking-[0.15em] text-graphite-600 dark:text-graphite-400 mb-2"
                                >
                                    Email
                                </label>
                                <input
                                    id="email"
                                    type="email"
                                    required
                                    autoComplete="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="w-full bg-white border border-graphite-300 text-graphite-900 placeholder-graphite-400 dark:bg-graphite-900 dark:border-graphite-700 dark:text-graphite-50 dark:placeholder-graphite-600 rounded px-3 py-2.5 focus:border-signal-500 focus:ring-0 focus:outline-none transition-colors"
                                    placeholder="andrea@ppcconcrete.com"
                                />
                            </div>

                            <div>
                                <div className="flex items-baseline justify-between mb-2">
                                    <label
                                        htmlFor="password"
                                        className="block font-mono text-[10px] uppercase tracking-[0.15em] text-graphite-600 dark:text-graphite-400"
                                    >
                                        Password
                                    </label>
                                    <button
                                        type="button"
                                        onClick={() => switchMode('forgot-password')}
                                        className="font-mono text-[10px] uppercase tracking-[0.15em] text-signal-600 hover:text-signal-700 dark:text-signal-500 dark:hover:text-signal-400 transition-colors"
                                    >
                                        Forgot?
                                    </button>
                                </div>
                                <input
                                    id="password"
                                    type="password"
                                    required
                                    autoComplete="current-password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="w-full bg-white border border-graphite-300 text-graphite-900 placeholder-graphite-400 dark:bg-graphite-900 dark:border-graphite-700 dark:text-graphite-50 dark:placeholder-graphite-600 rounded px-3 py-2.5 focus:border-signal-500 focus:ring-0 focus:outline-none transition-colors"
                                />
                            </div>

                            {error ? (
                                <div className="border-l-2 border-amber-500 bg-amber-500/10 px-3 py-2 text-sm text-amber-600 dark:text-amber-400">
                                    {error}
                                </div>
                            ) : null}

                            <button
                                type="submit"
                                disabled={submitting}
                                className="w-full bg-signal-500 hover:bg-signal-400 disabled:bg-graphite-300 dark:disabled:bg-graphite-700 disabled:cursor-not-allowed text-graphite-950 disabled:text-graphite-500 dark:disabled:text-graphite-950 font-semibold py-2.5 rounded transition-colors"
                            >
                                {submitting ? 'Signing in...' : 'Sign in'}
                            </button>
                        </form>
                    ) : resetSent ? (
                        <div className="space-y-4">
                            <div className="border-l-2 border-signal-500 bg-signal-500/10 px-3 py-3 text-sm text-graphite-900 dark:text-graphite-100">
                                Check your email. If an account exists for{' '}
                                <span className="font-mono text-graphite-900 dark:text-graphite-50">{email.trim()}</span>,
                                a reset link is on its way.
                            </div>
                            <button
                                type="button"
                                onClick={() => switchMode('sign-in')}
                                className="w-full border border-graphite-300 hover:border-graphite-500 dark:border-graphite-700 dark:hover:border-graphite-500 text-graphite-900 dark:text-graphite-100 font-semibold py-2.5 rounded transition-colors"
                            >
                                Back to Sign In
                            </button>
                        </div>
                    ) : (
                        <form onSubmit={handleSendReset} className="space-y-4">
                            <div>
                                <label
                                    htmlFor="reset-email"
                                    className="block font-mono text-[10px] uppercase tracking-[0.15em] text-graphite-600 dark:text-graphite-400 mb-2"
                                >
                                    Email
                                </label>
                                <input
                                    id="reset-email"
                                    type="email"
                                    required
                                    autoComplete="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="w-full bg-white border border-graphite-300 text-graphite-900 placeholder-graphite-400 dark:bg-graphite-900 dark:border-graphite-700 dark:text-graphite-50 dark:placeholder-graphite-600 rounded px-3 py-2.5 focus:border-signal-500 focus:ring-0 focus:outline-none transition-colors"
                                    placeholder="andrea@ppcconcrete.com"
                                />
                            </div>

                            {error ? (
                                <div className="border-l-2 border-amber-500 bg-amber-500/10 px-3 py-2 text-sm text-amber-600 dark:text-amber-400">
                                    {error}
                                </div>
                            ) : null}

                            <button
                                type="submit"
                                disabled={submitting}
                                className="w-full bg-signal-500 hover:bg-signal-400 disabled:bg-graphite-300 dark:disabled:bg-graphite-700 disabled:cursor-not-allowed text-graphite-950 disabled:text-graphite-500 dark:disabled:text-graphite-950 font-semibold py-2.5 rounded transition-colors"
                            >
                                {submitting ? 'Sending...' : 'Send Reset Link'}
                            </button>

                            <button
                                type="button"
                                onClick={() => switchMode('sign-in')}
                                className="w-full text-graphite-600 hover:text-graphite-900 dark:text-graphite-400 dark:hover:text-graphite-100 font-mono text-[10px] uppercase tracking-[0.15em] py-2 transition-colors"
                            >
                                Back to Sign In
                            </button>
                        </form>
                    )}

                    <div className="mt-10 font-mono text-[10px] text-graphite-400 dark:text-graphite-600 leading-relaxed">
                        Accounts are provisioned by the office.
                        <br />
                        Contact Andrea if you need access.
                    </div>
                </div>
            </div>

            <footer className="px-6 py-4 border-t border-graphite-200 dark:border-graphite-900 font-mono text-[10px] text-graphite-400 dark:text-graphite-600 flex items-center justify-between">
                <span>schedule.ppcconcrete.com</span>
                <span>v0.1</span>
            </footer>
        </div>
    );
}
